








































































































































































































































































































































































































































































































































































































































































import axios from "axios";
import Dinero from "dinero.js";
import {parse} from "date-fns";
import LocalForage from "localforage";
import {assign, Machine} from "xstate";
import {useMachine} from "@xstate/vue";
import {
  computed,
  defineComponent,
  ref,
  watch
} from "@vue/composition-api";

import store from "@/store/root";
import {auth as manager} from "@/auth";
import ModeButton from "@/components/ModeButton.vue";
import CodeSearch from "@/components/lien/CodeSearch.vue";
import ProcessStep from "@/components/steps/ProcessStep.vue";
import SettingsButton from "@/components/SettingsButton.vue";
import TableEntry from "@/components/lien/TableEntry.vue";
import AuthPanel from "@/components/AuthPanel.vue";
import IntersectionSentinel from "@/components/IntersectionSentinel.vue";
import {add, format} from "date-fns/esm";
import EditableInput from "@/components/lien/EditableInput.vue";
import EntryForm from "@/components/lien/EntryForm.vue";
import Tooltip from "@/components/Tooltip.vue";

interface Context {
  step: number;
  jobId: string;
  sessionId: string;
  file: File | null;
  endpoints: Record<string, string>;
}

const makeFormData = (data: [string, any][]) => {
  const result = new FormData();

  for (let [key, value] of data) {
    result.append(key, value);
  }

  return result;
};

const uploadFile = async (context: Context, event: any) => {
  if (!context.file) {
    return;
  }

  const checksum = Array.from(
    new Uint8Array(
      await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
    )
  )
    .map(byte => byte.toString(16).padStart(2, "0"))
    .join("");

  if (process.env.NODE_ENV !== "production") {
    const cached = ((await LocalForage.getItem<any[]>("LIEN_CACHE")) || [])
      .filter(
        entry =>
          process.env.NODE_ENV !== "production" ||
          (entry.version && entry.version === process.env.VUE_APP_COMMIT_ID)
      )
      .filter(entry => !entry.expires || entry.expires > new Date())
      .find(entry => entry.checksum === checksum);
    if (cached && cached.type === "results") {
      return {
        cached: true,
        sessionId: cached.sessionId,
        results: (await parseResults(cached.results)).results
      };
    }
    if (cached && cached.type === "id") return {
      cached: false,
      sessionId: cached.sessionId,
      jobId: cached.jobId
    };
  }

  const result = {
    cached: false,
    ...await axios
      .post("/jobs", makeFormData([["document", context.file], ["tool", "lien"]]), {
        baseURL: context.endpoints.textractor,
        headers: await manager.headers()
      })
      .then(response => ({
        sessionId: response.headers['x-session-id'],
        jobId: response.data.id
      }))
  };

  if (process.env.NODE_ENV !== "production") {
    await LocalForage.setItem(
      "LIEN_CACHE",
      [
        {
          checksum,
          type: "id",
          sessionId: result.sessionId,
          jobId: result.jobId,
          version: process.env.VUE_APP_COMMIT_ID,
          expires: add(new Date(), {hours: 24})
        },
        ...((await LocalForage.getItem<any[]>("LIEN_CACHE")) || [])
          .filter(entry => entry.checksum !== checksum)
          .filter(
            entry =>
              process.env.NODE_ENV !== "production" ||
              (entry.version && entry.version === process.env.VUE_APP_COMMIT_ID)
          )
          .filter(entry => !entry.expires || entry.expires > new Date())
      ].slice(0, 10)
    );
  }

  return result;
};

const processResults = async (context: any, event: any) => {
  const results = await axios
    .post(`/results`, {"id": context.jobId}, {
      headers: {'X-Session-Id': context.sessionId, ...await manager.headers()},
      baseURL: context.endpoints.textractor
    })
    .then(response => response.data);

  if (results.state !== "succeeded") {
    return {
      outcome: "error",
      type: "invalid_status",
      status:
        results.state === "in_progress"
          ? "IN_PROGRESS"
          : results.error.status,
      message:
        results.state === "in_progress"
          ? ""
          : results.error.message
    };
  }

  if (process.env.NODE_ENV !== "production") {
    const checksum = Array.from(
      new Uint8Array(
        await crypto.subtle.digest("SHA-256", await context.file.arrayBuffer())
      )
    )
      .map(byte => byte.toString(16).padStart(2, "0"))
      .join("");

    await LocalForage.setItem(
      "LIEN_CACHE",
      [
        {
          checksum,
          type: "results",
          version: process.env.VUE_APP_COMMIT_ID,
          sessionId: context.sessionId,
          results
        },
        ...((await LocalForage.getItem<any[]>("LIEN_CACHE")) || [])
          .filter(entry => entry.checksum !== checksum)
          .filter(
            entry =>
              process.env.NODE_ENV !== "production" ||
              (entry.version && entry.version === process.env.VUE_APP_COMMIT_ID)
          )
          .filter(entry => !entry.expires || entry.expires > new Date())
      ].slice(0, 10)
    );
  }

  return await parseResults(results);
};

const calculateFiscalYear = (date: Date) => date.getMonth() >= 9 ? date.getFullYear() + 1 : date.getFullYear();

const makeContext = (): Context => ({
  step: 1,
  jobId: "",
  sessionId: "",
  file: null,
  endpoints: store.state.settings.current.endpoints
});

const calculateCodeAsString = (code: any) => ({
  ...code,
  code: `${code.codeid}:${code.codeset}:${code.year}`
});

const expandDiagCodeDefinition = (entry: any) => (code: {
  value: string;
  bold: boolean;
}) =>
  calculateCodeAsString({
    bold: code.bold,
    codeid:
      entry.icdInd === "ICD-10"
        ? code.value
          .replace(/^1(.*)/, "I$1")
          .replace(/^2(.*)/, "Z$1")
        : code.value,
    codeset: entry.icdInd === "ICD-10" ? "icd10cm" : "icd9cm",
    year: String(
      sanitize(
        calculateFiscalYear(parse(entry.fromDate, "M/d/y", new Date())),
        entry.icdInd === "ICD-10"
          ? [2016, 2017, 2018, 2019, 2020, 2021]
          : [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014, 2015],
        entry.icdInd === "ICD-10" ? 2016 : 2005
      )
    )
  });

const isParseable = (raw: string) => raw.match(/([DH]):\s*:?(\w+)/) !== null;

const expandProcCodeDefinition = (entry: any) => (raw: string) => {
  const [_, system, code] = raw.match(/([DH]):\s*:?(\w+)/)!;

  return calculateCodeAsString({
    codeid: code,
    codeset: system === "H" ? "hcpcs" : "drg",
    year: String(
      sanitize(
        +entry.fromDate.split("/").pop(),
        system === "H" ? [2019] : [2021],
        system === "H" ? 2019 : 2021
      )
    )
  });
};

const parseResults = async (data: any) => {
  const results = data.results;

  const codes = Object.fromEntries(
    await axios
      .post(
        `${store.state.settings.current.endpoints.lien.replace(
          /^(.*)\/?$/,
          "$1"
        )}/codes`,
        {
          codes: Object.values(
            Object.fromEntries(
              results.summary.flatMap((entry: any) => [
                ...entry.dxCodes
                  .map(expandDiagCodeDefinition(entry))
                  .map((coding: any) => [coding.code, coding]),
                ...entry.pxCodes
                  .filter((raw: string) => isParseable(raw))
                  .map(expandProcCodeDefinition(entry))
                  .map((coding: any) => [coding.code, coding])
              ])
            )
          )
        },
        {
          headers: manager.enabled ? await manager.headers() : {}
        }
      )
      .then(response => response.data)
      .then(data =>
        data
          .map((code: any) => calculateCodeAsString(code))
          .map((code: any) => [code.code, calculateCodeAsString({
            ...code,
            year: sanitize<string>(code.year, code.codeset === 'icd9cm' ? ['2015'] : ['2019', '2020', '2021'], code.codeset === 'icd9cm' ? '2015' : '2019')
          })])
      )
  );

  return {
    outcome: "success",
    results: {
      meta: {
        ...results.meta,
        totalCpAmount: Dinero({
          amount: +results.meta.totalCpAmount.replace(".", ""),
          precision: 2
        }),
        numberOfEntries: results.meta.numberOfEntries || results.summary.length
      },
      warnings: results.warnings,
      summary: results.summary.map((entry: any) => ({
        ...entry,
        related: false,
        rationale: [],
        relatedMatch: null,
        notes: "",
        dxCodes: entry.dxCodes
          .map(expandDiagCodeDefinition(entry))
          .map((code: any, index) => ({
            ...codes[code.code],
            bold: results.meta.numberOfEntries === 0 ? index === 0 : code.bold
          })),
        pxCodes: entry.pxCodes
          .filter((raw: string) => isParseable(raw))
          .map(expandProcCodeDefinition(entry))
          .map((code: any) => codes[code.code]),
        fromDate: {
          parsed: parse(entry.fromDate, "M/d/y", new Date()),
          formatted: entry.fromDate
        },
        conditionalPayment: Dinero({
          amount: +entry.conditionalPayment.replace(".", ""),
          precision: 2
        }),
        totalCharges: Dinero({
          amount: +entry.totalCharges.replace(".", ""),
          precision: 2
        }),
        reimbursedAmount: Dinero({
          amount: +entry.reimbursedAmount.replace(".", ""),
          precision: 2
        })
      }))
    }
  };
};

interface Metadata {
  caseId?: string;
  caseType?: string;
  beneficiaryName?: string;
  beneficiaryId?: string;
  dateOfIncident?: string;
  totalCpAmount?: Dinero;
  numberOfEntries?: number;
}

interface LegacyCoding {
  bold?: boolean;
  code?: string;
  codeid: string;
  codename: string;
  codeset: string;
  found?: boolean;
  extended?: boolean;
  year: string;
}

interface Entry {
  icn: string;
  providerName: string;
  icdInd: string;
  dxCodes: LegacyCoding[];
  pxCodes: LegacyCoding[];
  fromDate: {
    parsed: Date;
    formatted: string;
  };
  toDate: string;
  conditionalPayment: Dinero.Dinero;
  related: boolean | null;
  rationale: number[];
  relatedMatch: any;
  notes: string;
}

const sanitize = <T>(value: T, possible: T[], fallback: T) =>
  possible.includes(value) ? value : fallback;

export default defineComponent({
  name: "LienView",
  components: {
    Tooltip,
    EntryForm,
    EditableInput,
    IntersectionSentinel,
    AuthPanel,
    TableEntry,
    SettingsButton,
    CodeSearch,
    ModeButton,
    ProcessStep
  },
  setup() {
    const addedEntriesCounter = ref<number>(0);
    const removedEntriesCounter = ref<number>(0);
    const meta = ref<Metadata>({});
    const warningsFromServer = ref<string[]>([]);
    const summary = ref<Entry[]>([]);
    const targets = ref<LegacyCoding[]>([]);
    const terminationDate = ref<string | null>(null);
    const mcsFileName = ref<string | null>("");
    const relatedness = ref([]);

    const machine = Machine<Context>({
      initial: "idle",
      context: makeContext(),
      states: {
        idle: {
          entry: [
            assign((context, event) => makeContext()),
            (context, event) => {
              addedEntriesCounter.value = 0;
              removedEntriesCounter.value = 0;
              mcsFileName.value = "";
              meta.value = {};
              warningsFromServer.value = [];
              summary.value = [];
              targets.value = [];
              terminationDate.value = null;
              relatedness.value = [];
            }
          ],
          on: {
            SELECT: "selected"
          }
        },
        selected: {
          entry: assign({file: (context, event) => event.file}),
          on: {
            UPLOAD: [
              {
                invoke: {},
                target: "uploading",
                cond: () => {
                }
              },
              {target: "uploading"}
            ],
            SELECT: "selected"
          }
        },
        uploading: {
          entry: assign({step: (context, event) => 2}),
          invoke: {
            src: uploadFile,
            onDone: [
              {
                target: "analysed",
                cond: (context, event) => event.data.cached,
                actions: [assign((context, event) => ({sessionId: event.data.sessionId})), (_, event) => {
                  warningsFromServer.value = event.data.results.warnings.map((warning: any) => warning.type);
                  meta.value = event.data.results.meta;
                  summary.value = event.data.results.summary.map((entry: any, index: number) => ({
                    ...entry,
                    index
                  }));
                }]
              },
              {
                target: "processing",
                actions: assign({
                  sessionId: (context, event) => event.data.sessionId,
                  jobId: (context, event) => event.data.jobId
                })
              }
            ],
            onError: {
              target: "failure"
            }
          }
        },
        processing: {
          entry: assign({step: (context, event) => 3}),
          invoke: {
            src: processResults,
            onDone: [
              {
                target: "analysed",
                cond: (context, event) => event.data.outcome === "success",
                actions: (_, event) => {
                  warningsFromServer.value = event.data.results.warnings.map((warning: any) => warning.type);
                  meta.value = event.data.results.meta;
                  summary.value = event.data.results.summary.map((entry: any, index: number) => ({
                    ...entry,
                    index
                  }));
                }
              },
              {
                target: "pending",
                cond: (context, event) =>
                  event.data.outcome === "error" &&
                  event.data.type === "invalid_status" &&
                  event.data.status === "IN_PROGRESS"
              }
            ],
            onError: {
              target: "failure"
            }
          }
        },
        pending: {
          after: {
            15000: "processing"
          }
        },
        analysed: {
          entry: assign((context, event) => ({step: 5})),
          on: {
            CLEAR: "idle"
          }
        },
        failure: {
          on: {
            RETRY: "idle"
          }
        }
      }
    });

    const {state, send} = useMachine(machine);

    const totalConditionalPayment = computed(() =>
      summary.value.reduce(
        (total, entry) => total.add(entry.conditionalPayment),
        Dinero()
      )
    );

    const reducedConditionalPayment = computed(() =>
      summary.value
        .filter(entry => !entry.related)
        .reduce((total, entry) => total.add(entry.conditionalPayment), Dinero())
    );

    const remainingConditionalPayment = computed(() =>
      totalConditionalPayment.value.subtract(reducedConditionalPayment.value)
    );

    const isLoading = ref(false);
    const isExporting = ref(false);

    const calculate = async () => {
      if (!state.value.matches("analysed")) return;

      isLoading.value = true;

      try {
        relatedness.value = await axios
          .post(
            `${store.state.settings.current.endpoints.lien.replace(
              /^(.*)\/?$/,
              "$1"
            )}/summary`,
            {
              targets: targets.value,
              candidates: summary.value
                .filter(entry => entry.dxCodes.length > 0)
                .flatMap(entry =>
                  entry.dxCodes
                    .filter((code) => code.bold)
                    .map(code => ({
                      codeid: code.codeid,
                      codeset: code.codeset,
                      year: code.year
                    }))
                )
            },
            {
              headers: manager.enabled ? await manager.headers() : {}
            }
          )
          .then(response => response.data.map(calculateCodeAsString))
          .catch(
            error => error.response &&
              error.response.status === 422
                ? []
                : Promise.reject(error)
          );
      } finally {
        isLoading.value = false;
      }
    };

    watch([() => terminationDate.value, () => relatedness.value], () => {
      const date = terminationDate.value
        ? parse(terminationDate.value, "y-M-d", new Date())
        : null;
      const relatedCodes = Object.fromEntries(
        relatedness.value
          .filter((code: any) => code.score === 1)
          .map((code: any) => [code.code, code])
      );
      const unrelatedCodes = new Set(
        relatedness.value
          .filter((code: any) => code.found || code.extended)
          .filter((code: any) => code.score !== 1)
          .map((code: any) => code.code)
      );

      summary.value.forEach(entry => {
        let result: [number[], any] = [[], null];

        const boldCodings: any[] = entry.dxCodes.filter(coding => coding.bold);

        if (date && entry.fromDate.parsed > date) result = [[0], null];
        for (const coding of boldCodings.filter(coding => coding.code in relatedCodes))
          result[1] = relatedCodes[coding.code].matchingtarget;
        if (boldCodings.length > 0 && boldCodings.every(coding => unrelatedCodes.has(coding.code)))
          result[0].push(1);

        entry.related = !result[0].includes(0) && (boldCodings.length === 0 || (relatedness.value && relatedness.value.length === 0)) ? null : result[0].length === 0;
        entry.rationale = result[0];
        entry.relatedMatch = result[1];
      });
    });

    const changeRelatedValueOfProvider = ({
                                            provider,
                                            value
                                          }: { provider: string, value: ["REMOVE" | "ADD", number] }) => {
      const normalize = (input: string) => input.replace(/[^0-9a-zA-Z]/g, "").toLocaleLowerCase();
      const normalizedProvider = normalize(provider);

      summary.value
        .filter(
          entry =>
            normalize(entry.providerName) === normalizedProvider
        )
        .forEach(entry => {
          entry.rationale = value[0] === "ADD" ? [...new Set([...entry.rationale, value[1]])] : entry.rationale.filter(rationale => rationale !== value[1]);
          entry.related = entry.rationale.length === 0;
        });
    };

    const changeRelatedValueOfCode = ({
                                        code,
                                        value
                                      }: { code: string, value: ["REMOVE" | "ADD", number] }) => {
      summary.value
        .filter(
          entry =>
            entry.dxCodes.length > 0 &&
            entry.dxCodes.some(coding => coding.bold && coding.code === code)
        )
        .forEach(entry => {
          entry.rationale = value[0] === "ADD" ? [...new Set([...entry.rationale, value[1]])] : entry.rationale.filter(rationale => rationale !== value[1]);
          entry.related = entry.rationale.length === 0;
        });
    };

    const remove = (entry: any) => {
      summary.value.splice(summary.value.findIndex(current => current === entry), 1);
      removedEntriesCounter.value++;
    };

    const add = (entry: any) => {
      summary.value.push(Object.assign(entry, {
        bold: entry.bold,
        relatedMatch: null,
        fromDate: {
          parsed: entry.fromDate,
          formatted: format(entry.fromDate, 'M/d/y')
        },
        toDate: format(entry.toDate, 'M/d/y')
      }));
      addedEntriesCounter.value++;
    }

    watch(() => targets.value, () => calculate());

    const collapseCodes = ref(false);

    const table = ref(null);

    const sortDirection = ref<"ASC" | "DESC" | null>(null);
    const sortBy = ref<keyof Entry | null>(null);

    const calculateKey = (obj: any, key: keyof Entry | null) =>
      key === "conditionalPayment"
        ? obj[key].toUnit(2)
        : key === "fromDate"
          ? obj[key].parsed
          : key === "toDate"
            ? parse(obj[key], "M/d/y", new Date())
            : key && obj[key] !== null
              ? +obj[key]
              : -1;

    const limit = computed(() => summary.value.length);

    const take = ref(100);

    const sorted = computed(() =>
      (sortDirection.value !== null && sortBy.value !== null
          ? summary.value
            .map(item => [item, calculateKey(item, sortBy.value)])
            .sort((a, b) =>
              sortDirection.value === "ASC" ? a[1] - b[1] : b[1] - a[1]
            )
            .map(tuple => tuple[0])
          : summary.value
      ).slice(0, take.value)
    );

    watch([() => sortBy.value, () => sortDirection.value], () => {
      take.value = 100;
    });

    const rationales = [
      "Past ORM term date",
      "Wrong diagnosis",
      "Provider not authorized",
      "Other"
    ];

    const exportToPdf = async () => {
      isExporting.value = true;

      axios
        .post(
          `/lien`,
          {
            reducedConditionalPayment: reducedConditionalPayment.value.toFormat(),
            totalConditionalPayment: totalConditionalPayment.value.toFormat(),
            remainingConditionalPayment: remainingConditionalPayment.value.toFormat(),
            acceptedCodings: targets.value.map(coding => ({
              code: coding.codeid,
              display: coding.codename,
              system: coding.codeset,
              version: coding.year
            })),
            meta: {
              mcsFileName: mcsFileName.value,
              terminationDate: terminationDate.value || "",
              ...meta.value,
              dateOfIncident: meta.value.dateOfIncident || "",
              beneficiaryId: meta.value.beneficiaryId || ""
            },
            entries: summary.value.map(entry => ({
              ...entry,
              conditionalPayment: entry.conditionalPayment.toFormat(),
              fromDate: entry.fromDate.formatted,
              rationale: (entry.rationale || []).map(rationale => rationales[rationale]),
              dxCodes: entry.dxCodes.map(coding => ({
                bold: coding.bold,
                code: coding.codeid.replace(/\./g, ""),
                display: coding.codename,
                system: coding.codeset,
                version: coding.year
              })),
              pxCodes: entry.pxCodes.map(coding => ({
                bold: coding.bold,
                code: coding.codeid.replace(/\./g, ""),
                display: coding.codename,
                system: coding.codeset,
                version: coding.year
              }))
            }))
          },
          {
            headers: {
              ...await manager.headers(),
              "X-Session-Id": state.value.context.sessionId
            },
            baseURL: state.value.context.endpoints.textractor,
            responseType: "blob"
          }
        )
        .then(response => {
          const link = document.createElement("a");

          link.href = URL.createObjectURL(new Blob([response.data]));
          link.download = state.value.context.file
            ? `${state.value.context.file.name.replace(
              /\.[^/.]+$/,
              ""
            )} - Verbotics.pdf`
            : "Verbotics.pdf";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .finally(() => (isExporting.value = false));
    };

    const validators: [() => boolean, string][] = [
      [() => Boolean(mcsFileName.value), "MCS file name must be filled."],
      [() => Boolean(meta.value.beneficiaryName), "Beneficiary name must be filled."],
      [() => Boolean(meta.value.beneficiaryId), "Beneficiary ID must be filled."],
      [() => Boolean(meta.value.caseId), "Case ID must be filled."],
      [() => Boolean(meta.value.caseType), "Case type must be filled."],
      [() => summary.value.every(entry => entry.related || (entry.related === false && (entry.rationale || []).length > 0)), "Every entry must have their relatedness indicated."]
    ];

    const integrityChecks: [() => boolean, () => any][] = [
      [() => !warningsFromServer.value.includes("INCONSISTENT_CP_AMOUNT") || !(Boolean(meta.value.totalCpAmount) && totalConditionalPayment.value.equalsTo(meta.value.totalCpAmount!)), () => ({
        type: "INCONSISTENT_CP_AMOUNT",
        expected: meta.value.totalCpAmount.toFormat(),
        computed: totalConditionalPayment.value.toFormat()
      })],
      [() => !warningsFromServer.value.includes("INCONSISTENT_ENTRIES_AMOUNT") || meta.value.numberOfEntries === summary.value.length, () => ({
        type: "INCONSISTENT_ENTRIES_AMOUNT",
        expected: meta.value.numberOfEntries,
        computed: summary.value.length
      })],
      [() => !summary.value.some(entry => entry.dxCodes.some(e => e.bold && !e.found)), () => ({
        type: "BOLD_CODE_NOT_COMPARABLE",
        entries: summary.value.map(entry => ({
          ...entry,
          found: entry.dxCodes.every(code => !code.bold || code.found)
        })).filter(entry => !entry.found)
      })],
      [() => summary.value.every(entry => ["ICD-10", "ICD-9"].includes(entry.icdInd)), () => ({
        type: "INVALID_CODE_SYSTEM",
        entries: summary.value.filter(entry => !["ICD-10", "ICD-9"].includes(entry.icdInd))
      })]
    ];

    const errors = computed(() => Object.keys(meta.value).length > 0 ? validators.filter(([check]) => !check()).map(([_, message]) => message) : []);

    const warnings = computed(() => Object.keys(meta.value).length > 0 ? integrityChecks.filter(([check]) => !check()).map(([_, message]) => message()) : []);

    return {
      add,
      remove,
      state,
      send,
      summary,
      meta,
      errors,
      totalConditionalPayment,
      reducedConditionalPayment,
      remainingConditionalPayment,
      terminationDate,
      mcsFileName,
      targets,
      isLoading,
      isExporting,
      collapseCodes,
      table,
      sorted,
      sortDirection,
      sortBy,
      take,
      limit,
      warnings,
      calculate,
      removedEntriesCounter,
      addedEntriesCounter,
      changeRelatedValueOfProvider,
      changeRelatedValueOfCode,
      confirm: (message: string) => window.confirm(message),
      exportToPdf
    };
  }
});
